/**
 * Sticky header for parent child list view: Tilbake + barn + stjerner (chrome)
 * and Dato + Legg til (sidestart – seksjonstitler ligger i ParentChildView).
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Calendar } from "@/components/ui/calendar";
import { CalendarDays, ChevronDown, ChevronLeft, ChevronRight, ListTodo, MessageSquare, Plus, Upload, BookOpen } from "lucide-react";
import { cn } from "@/lib/utils";
import { differenceInCalendarDays, isSameWeek, format } from "date-fns";
import { nb } from "date-fns/locale";

type Props = {
  childId: string;
  childName: string | null;
  avatarEmoji: string | null;
  pointsToday: number;
  /** "list" | "calendar" – when calendar, second row shows week pill instead of day pill */
  activeTab: "list" | "calendar";
  onTabChange: (tab: "list" | "calendar") => void;
  taskViewMode: "date" | "all";
  onTaskViewModeChange: (mode: "date" | "all") => void;
  dateOffset: number;
  onDateOffsetChange: (offset: number) => void;
  /** For calendar tab: week offset (0 = current week), label for pill, and change handler */
  weekOffset?: number;
  onWeekOffsetChange?: (offset: number) => void;
  weekLabel?: string;
  canAdd: boolean;
  hasPlus: boolean;
  hasTogether: boolean;
  maxHistoryDays: number;
  onOpenCreateTask: () => void;
  onOpenCalendar: () => void;
  onOpenAddReminder: () => void;
  onOpenAddHomework: () => void;
  onOpenPlanImport: () => void;
  /** When calendar tab: open add-event sheet in embedded calendar */
  onOpenCalendarAdd?: () => void;
  /** Opens dialog with detailed star status (count and when). */
  onOpenStarDetail?: () => void;
  /** Import quota for free users */
  importQuotaUsed?: number;
  importQuotaLimit?: number;
  importQuotaRemaining?: number;
};

export function ParentChildListHeader({
  childId,
  childName,
  avatarEmoji,
  pointsToday,
  activeTab,
  onTabChange,
  taskViewMode,
  onTaskViewModeChange,
  dateOffset,
  onDateOffsetChange,
  weekOffset = 0,
  onWeekOffsetChange,
  weekLabel,
  canAdd,
  hasPlus,
  hasTogether,
  maxHistoryDays,
  onOpenCreateTask,
  onOpenCalendar,
  onOpenAddReminder,
  onOpenAddHomework,
  onOpenPlanImport,
  onOpenCalendarAdd,
  onOpenStarDetail,
  importQuotaUsed,
  importQuotaLimit,
  importQuotaRemaining,
}: Props) {
  const navigate = useNavigate();
  const [datePopoverOpen, setDatePopoverOpen] = useState(false);
  const [weekPopoverOpen, setWeekPopoverOpen] = useState(false);
  const viewedDate = new Date();
  viewedDate.setDate(viewedDate.getDate() + dateOffset);
  const isToday = dateOffset === 0;

  const dayLabel = (() => {
    if (taskViewMode === "all") return "Alle oppgaver";
    if (dateOffset === 0) return "I dag";
    if (dateOffset === 1) return "I morgen";
    if (dateOffset === -1) return "I går";
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    if (isSameWeek(viewedDate, now, { weekStartsOn: 1 })) {
      return format(viewedDate, "EEEE", { locale: nb }).replace(/^\w/u, (c: string) => c.toUpperCase());
    }
    return format(viewedDate, "d. MMM", { locale: nb });
  })();

  return (
    <div className="sticky top-0 z-30">
      {/* Header: Tilbake + barn + stjerner */}
      <div className="border-b border-border bg-background/95 backdrop-blur-sm">
        <div className="px-4 pt-2 pb-1.5">
          <div className="mx-auto max-w-sm flex items-center justify-between">
            <Button variant="ghost" size="sm" className="h-7 gap-1 px-2 text-sm font-medium" onClick={() => navigate("/dashboard")}>
              Tilbake
            </Button>
            <div className="flex items-center gap-2">
              <span className="text-lg">{avatarEmoji || "🧒"}</span>
              <h1 className="text-lg font-heading font-bold truncate">{childName || "Barn"}</h1>
            </div>
            {onOpenStarDetail ? (
              <Button
                variant="ghost"
                size="sm"
                className="h-7 gap-1 rounded-full px-2 text-xs font-normal text-muted-foreground"
                onClick={onOpenStarDetail}
                aria-label="Se stjernestatus"
              >
                ⭐ {pointsToday}
              </Button>
            ) : (
              <div className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-normal text-muted-foreground">
                ⭐ {pointsToday}
              </div>
            )}
          </div>
        </div>
      </div>
      {/* Sidestart: Tabs + (Dato eller Uke) + Legg til */}
      <div className="bg-background">
        <div className="px-4 pt-1.5 pb-1.5">
          <div className="mx-auto max-w-sm flex items-center justify-between gap-2 min-w-0">
            {/* Tabs: Lister | Kalender */}
            <div className="flex rounded-full border border-border bg-muted/30 p-0.5 shrink-0">
              <button
                type="button"
                onClick={() => onTabChange("list")}
                className={cn(
                  "rounded-full px-3 py-1 text-xs font-medium transition-colors",
                  activeTab === "list"
                    ? "bg-background text-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground"
                )}
              >
                Lister
              </button>
              <button
                type="button"
                onClick={() => onTabChange("calendar")}
                className={cn(
                  "rounded-full px-3 py-1 text-xs font-medium transition-colors",
                  activeTab === "calendar"
                    ? "bg-background text-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground"
                )}
              >
                Kalender
              </button>
            </div>
            <div className="flex items-center gap-1.5 shrink-0">
              {activeTab === "list" && (
                <Popover open={datePopoverOpen} onOpenChange={setDatePopoverOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      className={cn(
                        "h-7 gap-1 rounded-full px-2.5 text-xs font-medium",
                        taskViewMode === "all" && "text-primary bg-primary/10",
                        taskViewMode === "date" && dateOffset < 0 && "text-destructive",
                        taskViewMode === "date" && dateOffset === 0 && "text-primary bg-primary/10",
                        taskViewMode === "date" && dateOffset === 1 && "text-amber-600 dark:text-amber-400",
                        taskViewMode === "date" && dateOffset > 1 && "text-muted-foreground"
                      )}
                    >
                      <CalendarDays className="h-3.5 w-3.5" />
                      {dayLabel}
                      <ChevronDown className="h-3 w-3 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 bg-popover z-50" align="end">
                    <div className="p-3 pb-2">
                      <span className="text-sm font-medium">Velg dag</span>
                    </div>
                    <div className="px-3 pb-2 flex flex-wrap gap-1.5">
                      <Button
                        variant={taskViewMode === "date" && dateOffset === 0 ? "secondary" : "ghost"}
                        size="sm"
                        className="h-7 text-xs"
                        onClick={() => {
                          onDateOffsetChange(0);
                          setDatePopoverOpen(false);
                        }}
                      >
                        I dag
                      </Button>
                      <Button
                        variant={taskViewMode === "date" && dateOffset === 1 ? "secondary" : "ghost"}
                        size="sm"
                        className="h-7 text-xs"
                        onClick={() => {
                          onDateOffsetChange(1);
                          setDatePopoverOpen(false);
                        }}
                      >
                        I morgen
                      </Button>
                      <Button
                        variant={taskViewMode === "all" ? "secondary" : "ghost"}
                        size="sm"
                        className="h-7 text-xs"
                        onClick={() => {
                          onTaskViewModeChange("all");
                          setDatePopoverOpen(false);
                        }}
                      >
                        Alle oppgaver
                      </Button>
                    </div>
                    <Calendar
                      mode="single"
                      selected={viewedDate}
                      onSelect={(date) => {
                        if (date) {
                          const today = new Date();
                          today.setHours(0, 0, 0, 0);
                          date.setHours(0, 0, 0, 0);
                          const diff = differenceInCalendarDays(date, today);
                          if (!hasPlus && diff < -maxHistoryDays) return;
                          onDateOffsetChange(diff);
                          setDatePopoverOpen(false);
                        }
                      }}
                      disabled={(date) => {
                        if (hasPlus) return false;
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        date.setHours(0, 0, 0, 0);
                        return differenceInCalendarDays(date, today) < -maxHistoryDays;
                      }}
                      className={cn("p-3 pointer-events-auto")}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              )}
              {activeTab === "calendar" && onWeekOffsetChange != null && weekLabel != null && (
                <Popover open={weekPopoverOpen} onOpenChange={setWeekPopoverOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      className={cn(
                        "h-7 gap-1 rounded-full px-2.5 text-xs font-medium",
                        weekOffset === 0 ? "text-primary bg-primary/10" : "text-muted-foreground"
                      )}
                    >
                      <CalendarDays className="h-3.5 w-3.5" />
                      {weekLabel}
                      <ChevronDown className="h-3 w-3 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 bg-popover z-50" align="end">
                    <div className="p-3 pb-2">
                      <span className="text-sm font-medium">Velg uke</span>
                    </div>
                    <div className="px-3 pb-2 flex items-center justify-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0"
                        onClick={() => onWeekOffsetChange(weekOffset - 1)}
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </Button>
                      <span className="text-sm min-w-[120px] text-center">{weekLabel}</span>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0"
                        onClick={() => onWeekOffsetChange(weekOffset + 1)}
                      >
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                    <Button
                      variant={weekOffset === 0 ? "secondary" : "ghost"}
                      size="sm"
                      className="w-full h-7 text-xs"
                      onClick={() => {
                        onWeekOffsetChange(0);
                        setWeekPopoverOpen(false);
                      }}
                    >
                      Denne uken
                    </Button>
                  </PopoverContent>
                </Popover>
              )}
              {(canAdd || activeTab === "calendar") && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="default" size="sm" className="rounded-full text-xs gap-1">
                      <Plus className="h-3.5 w-3.5" /> Legg til
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48">
                    <DropdownMenuItem onClick={onOpenCreateTask}>
                      <ListTodo className="h-4 w-4 mr-2" />
                      Oppgave
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={activeTab === "calendar" && onOpenCalendarAdd ? onOpenCalendarAdd : onOpenCalendar}>
                      <CalendarDays className="h-4 w-4 mr-2" />
                      Hendelse
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={onOpenAddReminder}>
                      <MessageSquare className="h-4 w-4 mr-2" />
                      Påminnelse
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={onOpenAddHomework}>
                      <BookOpen className="h-4 w-4 mr-2" />
                      Lekse
                    </DropdownMenuItem>
                    {(hasPlus || hasTogether) ? (
                      <DropdownMenuItem onClick={onOpenPlanImport}>
                        <Upload className="h-4 w-4 mr-2" />
                        Importer ukeplan
                      </DropdownMenuItem>
                    ) : importQuotaRemaining != null && importQuotaRemaining > 0 ? (
                      <DropdownMenuItem onClick={onOpenPlanImport}>
                        <Upload className="h-4 w-4 mr-2" />
                        <span className="flex-1">Importer ukeplan</span>
                        <Badge variant="secondary" className="text-[10px] px-1.5 py-0 font-normal">
                          {importQuotaUsed}/{importQuotaLimit}
                        </Badge>
                      </DropdownMenuItem>
                    ) : (
                      <DropdownMenuItem disabled className="opacity-100">
                        <Upload className="h-4 w-4 mr-2 text-muted-foreground" />
                        <span className="flex-1">Importer ukeplan</span>
                        <Badge variant="secondary" className="text-[10px] px-1.5 py-0 font-normal">
                          Pluss
                        </Badge>
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
